import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { GifList, IUserList } from "./GifList";
import { useSearchGifsQuery } from "../../queries";
import { useQuery } from "hooks/useQuery";
import { ViewRegion } from "utils/metrics";

import "./TagGifList.scss";
import { IGif, INicheMap } from "services/types";
import { filterGifsList } from "utils/filterGifsList";
import { filterNichesList } from "utils/filterNichesList";
import { filterUsersList } from "utils/filterUsersList";
import { routes } from "routes";

interface IProps {
  tag: string;
}

export const TagGifList = (props: IProps) => {
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(1);
  const [totalGifs, setTotalGifs] = useState<number>(0);
  const [gifs, setGifs] = useState<IGif[]>([]);
  const [users, setUsers] = useState<IUserList>({});
  const [topic, setTopic] = useState<string>("a");
  const [order, setOrder] = useState("trending");
  const [tiles, setTiles] = useState<boolean>(true);
  const [verified, setVerified] = useState<boolean>(false);
  const [niches, setNiches] = useState<INicheMap>({});
  const navigate = useNavigate();
  const query = useQuery();

  const { data, isSuccess, isError, isLoading } = useSearchGifsQuery({
    tag: props.tag,
    count: 40,
    type: topic === "a" ? undefined : topic,
    order,
    page: page,
    verified: verified,
  });

  const reload = useCallback(() => {
    setTotalGifs(0);
    setGifs([]);
    setPage(1);
    window.scrollTo(0, 0);
  }, []);

  const changeTopic = useCallback((value: string) => {
    console.debug(`[TagGifList] topic changed to ${value}`);
    setTopic(value);
    reload();
  }, [reload]);

  const changeOrder = useCallback((value: string) => {
    console.debug(`[TagGifList] order changed to ${value}`);
    const verifiedParam = !!query.get("verified") || verified;

    navigate(routes.singleTag(props.tag, value, verifiedParam));
    setOrder(value);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, verified]);

  useEffect(() => {
    reload();
  }, [props.tag, reload]);

  useEffect(() => {
    if (isSuccess && data !== undefined) {
      setGifs(prev => filterGifsList(prev, data.gifs));
      setPages(data.pages);
      setTotalGifs(data.total);
      setUsers(prev => filterUsersList(prev, data.users));

      if (data.niches) {
        setNiches(prev => filterNichesList(prev, data.niches));
      }
    }
  }, [data, isSuccess]);

  useEffect(() => {
    const orderParam = query.get("order");
    const verifiedParam = query.get("verified");

    if (orderParam) {
      changeOrder(orderParam);
    }

    if (verifiedParam) {
      changeVerified(!!verifiedParam);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeMode = useCallback((mode: boolean) => {
    console.debug(`[TagGifList] tiles changed to ${mode}`);
    setTiles(mode);
  }, []);

  const fetchMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const changeVerified = useCallback((value: boolean) => {
    const orderParam = query.get("order") || order;
    navigate(routes.singleTag(props.tag, orderParam, value));
    setVerified(value);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, order]);

  return (
    <div className="tagGifList">
      <GifList
        topic={topic}
        order={order}
        ordering={true}
        orderingTop={true}
        tiles={tiles}
        verified={verified}
        gifs={gifs}
        users={users}
        niches={niches}
        totalLength={totalGifs}
        hasMore={page < pages}
        changeTopic={changeTopic}
        changeOrder={changeOrder}
        changeMode={changeMode}
        changeVerified={changeVerified}
        fetchMore={fetchMore}
        isError={isError}
        isLoading={isLoading}
        source={ViewRegion.Search}
        rel={`tag:${props.tag},${topic}`}
      />
    </div>
  );
};
